import React, { useEffect, useState } from "react";
import NinaDailyMarketScanner from "./NinaDailyMarketScanner";
import NinaLiveAfterHoursScanner from "./NinaLiveAfterHoursScanner";
import NinaLiveOvernightScanner from "./NinaLiveOvernightScanner";
import NinaLiveDayScanner from "./NinaLiveDayScanner";
import NinaLivePremarketScanner from "./NinaLivePremarketScanner";
import NinaOptionsOpportunityEngine from "./NinaOptionsOpportunityEngine";
import ScannerFeatureGuide from "./ScannerFeatureGuide";
import "./NinaScannerWorkspace.css";
import "./NinaScannerHandoff.css";
import "./ScannerRestoration.css";

const SCANNER_MODES = [
  {
    id: "day",
    label: "Day Trading",
    requiredFeature: "basic_market_scanner",
    status: "LIVE",
    title: "Authenticated Alpaca stock snapshots connected",
  },
  {
    id: "signature",
    label: "AI Daily Market Scanner",
    requiredFeature: "ranked_market_opportunities",
    status: "SIGNATURE",
    title:
      "Production signature scanner with 5-minute discovery, three ranked opportunities, and confirmation alerts",
  },
  {
    id: "premarket",
    label: "Premarket",
    requiredFeature: "premarket_intelligence",
    status: "LIVE",
    displayStatus: "15-MIN DELAY",
    title: "Authenticated Alpaca premarket snapshots connected",
  },
  {
    id: "after-hours",
    label: "After-Hours",
    requiredFeature: "after_hours_intelligence",
    status: "LIVE",
    displayStatus: "15-MIN DELAY",
    title: "Authenticated Alpaca after-hours snapshots connected",
  },
  {
    id: "overnight",
    label: "Overnight",
    requiredFeature: "overnight_intelligence",
    status: "LIVE",
    title: "Authenticated Alpaca BOATS overnight bars connected",
  },
  {
    id: "options-engine",
    label: "Options Engine",
    requiredFeature: "options_intelligence_practice",
    status: "INDICATIVE",
    title:
      "Authenticated Alpaca indicative option-chain review with contract metadata when available; executable OPRA remains unavailable",
  },
];

function ScannerModeBoundary({ scannerMode, children }) {
  if (scannerMode.id !== "options-engine") {
    return children;
  }

  return (
    <div className="scannerPracticeBoundary">
      <div className="scannerDemoNotice" role="note">
        <strong>AUTHENTICATED INDICATIVE OPTIONS</strong>
        <span>
          Real Alpaca indicative option-chain snapshots and contract metadata are
          connected when available. Executable OPRA data and brokerage execution
          are unavailable, every contract remains WAIT, and no order is placed.
        </span>
      </div>
      {children}
    </div>
  );
}

function validHandoffSymbol(value) {
  return /^[A-Z][A-Z0-9.-]{0,9}$/.test(
    String(value || "")
      .trim()
      .toUpperCase(),
  );
}

function finiteNumber(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function authenticatedSeedFromReview(review, scannerMode) {
  if (
    !["LIVE", "SIGNATURE"].includes(scannerMode?.status) ||
    review?.evidenceStatus === "simulated"
  ) {
    return null;
  }

  const currentPrice = finiteNumber(
    review?.currentPrice ?? review?.lastPrice ?? review?.latestPrice,
  );

  if (currentPrice === null) return null;

  return {
    symbol: String(review.symbol || "")
      .trim()
      .toUpperCase(),
    currentPrice,
    regularClose: finiteNumber(review?.regularClose),
    previousClose: finiteNumber(review?.previousClose),
    bid: finiteNumber(review?.bid),
    ask: finiteNumber(review?.ask),
    volume: finiteNumber(review?.volume),
    vwap: finiteNumber(review?.vwap),
    updatedAt: review?.updatedAt || null,
    provider: review?.provider || "Alpaca",
    feed: review?.feed || "",
    quality:
      review?.dataQuality ||
      review?.quality ||
      "Authenticated scanner evidence",
    notice:
      review?.notice ||
      "Scanner handoff seed. Fresh authenticated Live Trading snapshot is loading.",
  };
}

function ScannerModeContent({
  scannerMode,
  scannerHandoff,
  isActive,
  signatureOpportunities,
  onSignatureOpportunitiesChange,
}) {
  if (scannerMode.id === "signature") {
    return (
      <NinaDailyMarketScanner
        onOpenTradeReview={scannerHandoff}
        onOpportunitiesChange={onSignatureOpportunitiesChange}
      />
    );
  }
  if (scannerMode.id === "day") {
    return (
      <NinaLiveDayScanner
        onOpenTradeReview={scannerHandoff}
        isActive={isActive}
      />
    );
  }

  if (scannerMode.id === "premarket") {
    return <NinaLivePremarketScanner onOpenTradeReview={scannerHandoff} />;
  }

  if (scannerMode.id === "after-hours") {
    return <NinaLiveAfterHoursScanner onOpenTradeReview={scannerHandoff} />;
  }

  if (scannerMode.id === "overnight")
    return <NinaLiveOvernightScanner onOpenTradeReview={scannerHandoff} />;

  if (scannerMode.id === "options-engine") {
    return (
      <NinaOptionsOpportunityEngine
        onOpenTradeReview={scannerHandoff}
        signatureOpportunities={signatureOpportunities}
      />
    );
  }

  return null;
}

export default function NinaScannerWorkspace({
  onOpenTradeReview,
  isActive = true,
  featureAccess = null,
}) {
  const [mode, setMode] = useState("day");
  const [visitedModes, setVisitedModes] = useState(() => new Set(["day"]));
  const [guideOpen, setGuideOpen] = useState(false);
  const [handoffError, setHandoffError] = useState("");
  const [signatureOpportunities, setSignatureOpportunities] = useState([]);

  const activateMode = (nextMode) => {
    setMode(nextMode);

    setVisitedModes((current) => {
      if (current.has(nextMode)) return current;

      const next = new Set(current);
      next.add(nextMode);
      return next;
    });
  };

  useEffect(() => {
    const restoreMode = (event) => {
      const requestedModeValue = String(event.detail?.mode || "");
      const requestedMode =
        requestedModeValue === "options"
          ? "options-engine"
          : requestedModeValue;

      if (
        SCANNER_MODES.some((scannerMode) => scannerMode.id === requestedMode)
      ) {
        activateMode(requestedMode);
      }
    };

    window.addEventListener("nina:scanner-restore-mode", restoreMode);

    return () =>
      window.removeEventListener("nina:scanner-restore-mode", restoreMode);
  }, []);

  const openInAuthenticatedLiveTrading = (review) => {
    const symbol = String(review?.symbol || "")
      .trim()
      .toUpperCase();

    if (!validHandoffSymbol(symbol)) {
      const error = "Scanner handoff requires a valid stock symbol.";
      setHandoffError(error);
      return { ok: false, error };
    }

    const scannerMode =
      SCANNER_MODES.find((candidate) => candidate.id === mode) ||
      SCANNER_MODES[0];

    const seed = authenticatedSeedFromReview(review, scannerMode);

    setHandoffError("");

    try {
      localStorage.setItem("aninaSelectedSymbol", symbol);
    } catch (error) {}

    window.dispatchEvent(
      new CustomEvent("nina:symbol-change", {
        detail: { symbol },
      }),
    );

    window.dispatchEvent(
      new CustomEvent("nina:scanner-open-live", {
        detail: {
          symbol,
          scannerKind: review?.kind || mode,
          evidenceStatus: review?.evidenceStatus || null,
          origin: {
            mode: scannerMode.id,
            label: scannerMode.label,
            status: scannerMode.status,
          },
          seed,
          review,
        },
      }),
    );

    return { ok: true, warning: "" };
  };

  const scannerHandoff = (review) => {
    const response = openInAuthenticatedLiveTrading(review);

    if (response.ok || typeof onOpenTradeReview !== "function") {
      return response;
    }

    return onOpenTradeReview(review);
  };

  return (
    <section
      className="ninaScannerWorkspace"
      aria-labelledby="scanner-workspace-title"
    >
      <header className="scannerWorkspaceHeader">
        <div className="scannerWorkspaceTitleRow">
          <div>
            <span>PRODUCTION SCANNER WORKSPACE</span>
            <h1 id="scanner-workspace-title">AI Market Scanner</h1>
          </div>

          <button
            type="button"
            className="scannerFeatureGuideButton"
            aria-expanded={guideOpen}
            aria-controls="scanner-feature-guide"
            onClick={() => setGuideOpen((current) => !current)}
          >
            <span aria-hidden="true">?</span>
            Feature Guide
          </button>
        </div>

        <div
          className="scannerModeTabs"
          role="tablist"
          aria-label="Scanner mode"
        >
          {SCANNER_MODES.map((scannerMode) => (
            <button
              id={`scanner-mode-${scannerMode.id}`}
              key={scannerMode.id}
              type="button"
              disabled={Boolean(
                scannerMode.requiredFeature &&
                featureAccess &&
                featureAccess[scannerMode.requiredFeature] !== true
              )}
              role="tab"
              className={
                scannerMode.id === "signature"
                  ? "scannerSignatureTab"
                  : undefined
              }
              aria-selected={mode === scannerMode.id}
              aria-controls={`scanner-panel-${scannerMode.id}`}
              onClick={() => activateMode(scannerMode.id)}
              title={scannerMode.title}
            >
              {scannerMode.label} ·{" "}
              {scannerMode.displayStatus || scannerMode.status}
            </button>
          ))}
        </div>

        <p>
          Day Trading, Premarket, After-Hours, and Overnight use authenticated
          Alpaca stock data. Options Engine reviews authenticated Alpaca
          indicative option chains for up to ten stock symbols and remains
          non-executable because executable OPRA data and brokerage execution
          are unavailable.
        </p>
      </header>

      {handoffError && (
        <div className="scannerError" role="alert">
          {handoffError}
        </div>
      )}

      {guideOpen && <ScannerFeatureGuide onClose={() => setGuideOpen(false)} />}

      {SCANNER_MODES.map((scannerMode) => (
        <div
          id={`scanner-panel-${scannerMode.id}`}
          key={scannerMode.id}
          role="tabpanel"
          aria-labelledby={`scanner-mode-${scannerMode.id}`}
          hidden={mode !== scannerMode.id}
        >
          {visitedModes.has(scannerMode.id) ? (
            <ScannerModeBoundary scannerMode={scannerMode}>
              <ScannerModeContent
                scannerMode={scannerMode}
                scannerHandoff={scannerHandoff}
                isActive={isActive && mode === scannerMode.id}
                signatureOpportunities={signatureOpportunities}
                onSignatureOpportunitiesChange={setSignatureOpportunities}
              />
            </ScannerModeBoundary>
          ) : null}
        </div>
      ))}
    </section>
  );
}
